/* ──────────────────────────────────────────────────────────────────────────
 * Oracle {IPO.GPT} — India's IPO GPT / IPO LLM.
 * Full-width, single-column chat. NO left sidebar or panels. Web-grounded over
 * SEBI, NSE, BSE, NSDL, Chittorgarh, ipoplatform.com & the wider internet.
 * Ported from `oracle-ipo-gpt - claude.html`.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Home, MessageCircle, Send, ChevronDown, Plus, Globe, ShieldCheck } from "lucide-react";
import ContactSalesDialog from "@/components/ContactSalesDialog";
import { Seo } from "@/components/Seo";
import "./OraclePage.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCES = [
  { key: "all", label: "All Sources" },
  { key: "sebi", label: "SEBI Filings" },
  { key: "drhp", label: "DRHP Documents" },
  { key: "gmp", label: "GMP & Listing Data" },
  { key: "research", label: "Market Research" },
];

const SUGGESTS = [
  "Latest SEBI regulations for SME IPO listings",
  "Current IPO GMP & subscription status",
  "How to read a DRHP — key sections to check",
  "Upcoming mainboard IPOs on NSE & BSE",
];

const domainOf = (url) => {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return "source"; }
};

const ORACLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Oracle IPO.GPT",
  alternateName: ["IPO GPT", "IPO LLM", "IPO Intelligence"],
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  url: "https://www.ipo-labs.com/oracle",
  description:
    "Oracle IPO.GPT is India's dedicated IPO GPT / IPO LLM — an AI assistant that answers Indian IPO, DRHP, SEBI, NSE, BSE, GMP and pre-IPO funding questions with live web grounding across SEBI, NSDL, Chittorgarh and more.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
  publisher: { "@type": "Organization", name: "IPOLabs", url: "https://www.ipo-labs.com" },
};

const OraclePage = ({ user, apiClient }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [source, setSource] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const endRef = useRef(null);
  const taRef = useRef(null);
  const reqRef = useRef(0);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  // Auto-send a query passed via ?q=
  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q");
    if (q && q.trim()) send(q.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (raw) => {
    const query = (raw ?? input).trim();
    if (!query || loading) return;
    const myReq = ++reqRef.current;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";
    setMessages((m) => [...m, { role: "user", content: query }]);
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/oracle/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ query, thread_id: threadId, mode: "general", source_filter: source }),
      });
      if (!res.ok) throw new Error("chat failed");
      const data = await res.json();
      if (reqRef.current !== myReq) return; // superseded by New chat / newer query
      setThreadId(data.thread_id);
      const msg = data.message || {};
      setMessages((m) => [...m, {
        role: "assistant",
        content: msg.content || "I couldn't generate a response. Please try again.",
        grounded: !!msg.grounded,
        references: msg.references || [],
        follow_ups: msg.follow_ups || [],
      }]);
    } catch {
      if (reqRef.current !== myReq) return;
      toast.error("Oracle couldn't answer that. Please try again.");
      setMessages((m) => [...m, {
        role: "assistant", content: "Sorry — I hit a snag reaching my sources. Please try again in a moment.",
        grounded: false, references: [], follow_ups: [],
      }]);
    } finally {
      if (reqRef.current === myReq) setLoading(false);
    }
  };

  const newChat = () => { reqRef.current++; setThreadId(null); setMessages([]); setInput(""); setLoading(false); };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };
  const autoGrow = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 160) + "px";
  };

  const activeSource = SOURCES.find((s) => s.key === source) || SOURCES[0];

  return (
    <div className="orc-page">
      <Seo
        title="Oracle IPO.GPT — India's IPO AI Assistant | IPO GPT & IPO LLM"
        description="Oracle IPO.GPT is India's dedicated IPO GPT / IPO LLM. Ask anything about Indian IPOs, DRHPs, SEBI/NSE/BSE filings, GMP, allotment and pre-IPO funding — answered with live web search across SEBI, NSDL, Chittorgarh and more."
        path="/oracle"
        jsonLd={ORACLE_JSONLD}
      />
      <div className="orc-root">
        <div className="orc-main">
          {/* Topbar */}
          <div className="orc-topbar">
            <div className="orc-topbar-left">
              <div className="orc-mark">
                <div className="orc-mark-glyph">O</div>
                <div className="orc-mark-text">Oracle <span className="braces">&#123;IPO.GPT&#125;</span></div>
              </div>
              <div className="orc-topsep" />
              <button className="orc-topbtn" onClick={() => navigate("/home")} data-testid="oracle-home-btn"><Home size={15} /> <span>Home Page</span></button>
              <button className="orc-topbtn" onClick={() => setContactOpen(true)} data-testid="oracle-contact-btn"><MessageCircle size={15} /> <span>Contact Us</span></button>
              <button className="orc-topbtn" onClick={newChat} data-testid="oracle-new-chat-btn"><Plus size={15} /> <span>New chat</span></button>
            </div>

            <div className="orc-filter">
              <button className="orc-filter-btn" onClick={() => setFilterOpen((o) => !o)} data-testid="oracle-source-btn">
                <Globe size={14} /> {activeSource.label} <ChevronDown size={14} />
              </button>
              {filterOpen && (
                <div className="orc-filter-menu" data-testid="oracle-source-menu">
                  {SOURCES.map((s) => (
                    <div
                      key={s.key}
                      className={`orc-filter-opt ${source === s.key ? "active" : ""}`}
                      onClick={() => { setSource(s.key); setFilterOpen(false); }}
                      data-testid={`oracle-source-${s.key}`}
                    >
                      {s.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Thread */}
          <div className="orc-thread" data-testid="oracle-thread">
            {messages.length === 0 && !loading && (
              <div className="orc-intro">
                <h1 className="orc-intro-h serif">Ask Oracle <span className="braces">&#123;IPO.GPT&#125;</span></h1>
                <p className="orc-intro-p">
                  India's IPO GPT — a specialised IPO LLM. Ask about DRHPs, SEBI/NSE/BSE rules, GMP,
                  allotment, valuations and pre-IPO funding. Answers are grounded with live web search
                  across SEBI, NSDL, Chittorgarh and more.
                </p>
                <div className="orc-suggests">
                  {SUGGESTS.map((q) => (
                    <button key={q} className="orc-suggest" onClick={() => send(q)} data-testid="oracle-suggest">{q}</button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`orc-msg-row ${m.role}`}>
                {m.role === "assistant" && <div className="orc-avatar">O</div>}
                <div className="orc-bubble-col">
                  {m.role === "assistant" && (
                    <span className={`orc-badge ${m.grounded ? "live" : "model"}`}>
                      {m.grounded ? <><ShieldCheck size={12} /> Cross-checked with live search</> : <>General knowledge — verify against filing</>}
                    </span>
                  )}
                  <div className="orc-bubble">
                    {m.role === "assistant"
                      ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                      : m.content}
                  </div>
                  {m.role === "assistant" && m.references?.length > 0 && (
                    <div className="orc-refs">
                      {m.references.slice(0, 6).map((r, ri) => (
                        r.url
                          ? <a key={ri} className="orc-ref" href={r.url} target="_blank" rel="noreferrer">{r.grade?.emoji || "🔗"} {domainOf(r.url)}</a>
                          : <span key={ri} className="orc-ref">{r.grade?.emoji || "📄"} {r.label}</span>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && m.follow_ups?.length > 0 && (
                    <div className="orc-followups">
                      {m.follow_ups.slice(0, 3).map((f, fi) => (
                        <button key={fi} className="orc-fu" onClick={() => send(f)} data-testid="oracle-followup">{f}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="orc-msg-row assistant">
                <div className="orc-avatar">O</div>
                <div className="orc-bubble-col">
                  <div className="orc-bubble"><span className="orc-typing"><span /><span /><span /></span></div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Composer */}
          <div className="orc-composer-wrap">
            <div className="orc-composer">
              <div className="orc-input-bar">
                <textarea
                  ref={taRef}
                  rows={1}
                  placeholder="Ask about any IPO, DRHP, SEBI rule, GMP or pre-IPO round…"
                  value={input}
                  onChange={autoGrow}
                  onKeyDown={onKey}
                  data-testid="oracle-input"
                />
                <button className="orc-send" onClick={() => send()} disabled={loading || !input.trim()} data-testid="oracle-send-btn">
                  <Send size={16} />
                </button>
              </div>
              <div className="orc-disclaimer">
                Oracle IPO.GPT can make mistakes. Verify critical figures against the official SEBI / NSE / BSE filing before acting.
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactSalesDialog open={contactOpen} onOpenChange={setContactOpen} apiClient={apiClient} user={user} defaultModule="Oracle IPO.GPT" />
    </div>
  );
};

export default OraclePage;
